import type { KnowledgeDocument } from '@ralph/contracts'
import { foldForSearch } from '@/i18n/format.ts'

/**
 * The pure half of a knowledge base's documents view.
 *
 * Everything here answers "given these rows, what is true about this knowledge base" and
 * returns a STATE or a COUNT — never a component, and since PRD-27 never a sentence either.
 * The words that describe these numbers are catalogue copy (`i18n/messages/<locale>/knowledge`,
 * LOC-010), so this module is free of locale entirely: it holds no `t`, no formatter and no
 * plural rule, and its spec asserts a shape rather than a paragraph of prose (D-23).
 * (What is true about a base as a whole — why a reader has it, its limits, the decisions about
 * who can use it — lives in `knowledge-base.ts`.)
 *
 * The lies, in the order they were found:
 *
 *   1. `documentCount` counts rows, not answers. A base holding three documents of which
 *      one failed reported "DOCS 3" beside a green card, and the only trace of the failure
 *      was a red badge four rows down. `indexHealth` is the count a maintainer actually needs:
 *      how much of this base can be cited today.
 *   2. A document can be `indexed` and hold no passages. Search reads passages, so such a
 *      row is in the catalogue, looks healthy, and can never be cited — it is silent. It gets
 *      its own standing rather than being coloured like the ones that work.
 *   3. `quarantined` was rendered in the same blue as `chunking`. One of those is a file
 *      being worked on and the other is a file the malware scan refused; they cannot share a
 *      colour.
 *   4. The failure reason was the server's own sentence and nothing else — accurate, and no
 *      help. `diagnoseFailure` names the remedy that is true for it, for the handful of causes
 *      where there is something to do.
 */

// ---------------------------------------------------------------------------
// Standing
// ---------------------------------------------------------------------------

/**
 * What a document is doing for the agent, which is not the same question as what the
 * ingestion pipeline last did to it.
 *
 * `answering` is the only standing that means "an agent can cite this". Everything else is a
 * reason it cannot, kept apart because the remedies differ: wait, fix the file, or call
 * security.
 */
export type Standing = 'answering' | 'working' | 'silent' | 'unreadable' | 'quarantined'

export const standingOf = (document: Pick<KnowledgeDocument, 'status' | 'chunkCount'>): Standing => {
  switch (document.status) {
    case 'indexed':
      // Indexed with nothing to retrieve. The pipeline refuses to finish this way (a document
      // that chunks to nothing is failed outright), so reaching it means something interrupted
      // the run — and the row must not wear the colour of a document that answers.
      return document.chunkCount > 0 ? 'answering' : 'silent'
    case 'failed':
      return 'unreadable'
    case 'quarantined':
      return 'quarantined'
    case 'deleted':
      return 'silent'
    default:
      return 'working'
  }
}

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

export type IndexHealth = {
  total: number
  answering: number
  working: number
  silent: number
  unreadable: number
  quarantined: number
  /** Passages held by the documents that are actually answering. */
  passages: number
}

const EMPTY_HEALTH: IndexHealth = {
  total: 0,
  answering: 0,
  working: 0,
  silent: 0,
  unreadable: 0,
  quarantined: 0,
  passages: 0
}

export const indexHealth = (documents: KnowledgeDocument[]): IndexHealth => {
  const health: IndexHealth = { ...EMPTY_HEALTH }
  for (const document of documents) {
    const standing = standingOf(document)
    health.total += 1
    health[standing] += 1
    if (standing === 'answering') health.passages += document.chunkCount
  }
  return health
}

/** True when something in this knowledge base needs a person, as opposed to needing time. */
export const needsAttention = (health: IndexHealth): boolean =>
  health.unreadable > 0 || health.quarantined > 0 || health.silent > 0

// ---------------------------------------------------------------------------
// Failure diagnosis
// ---------------------------------------------------------------------------

/**
 * Why a file could not be read, when the reason is one a person can act on.
 *
 * The server's sentence is always shown verbatim — it is the record, and guessing over it
 * would be worse than useless the day the pipeline grows a failure this list has never heard
 * of. What is added is the next move, for the causes where there is one.
 *
 * The maker plane classifies the same failures in `marketplace/maker/knowledge-coverage.ts`
 * under a vocabulary that forbids mechanism words. These are deliberately two lists: the
 * remedies keyed here may say "OCR" because the person reading them maintains the documents.
 */
export type FailureCause =
  | 'scan'
  | 'empty'
  | 'unsupported'
  | 'legacy'
  | 'protected'
  | 'timeout'
  | 'too_large'
  | 'superseded'
  | 'lost'
  | 'unknown'

/**
 * A failure classified, with the key of the remedy that is true for it.
 *
 * `remedy` is a KEY, never a sentence (PRD-27 D-6, D-23). The words live in
 * `i18n/messages/<locale>/knowledge.ts` with the rest of the copy, so this module classifies
 * and the catalogue speaks — and so the spec asserts a shape rather than a paragraph of prose.
 *
 * It is not the same axis as `cause`: two different scans fail for reasons a maintainer acts
 * on differently (turn OCR on, versus rescan the paper), and both are `scan`.
 */
export type RemedyKey =
  | 'scan_off'
  | 'scan_unreadable'
  | 'legacy'
  | 'protected'
  | 'timeout'
  | 'too_large'
  | 'empty'
  | 'unsupported'
  | 'lost'
  | 'superseded'

export type Diagnosis = {
  cause: FailureCause
  /** Which remedy to show. Null when the console has nothing honest to add. */
  remedy: RemedyKey | null
}

/**
 * ── A bridge, and it is meant to be deleted ──
 *
 * Choosing what to say by matching what the server said is the failure mode PRD-27 D-6 bans
 * outright, and the day either side is localized every one of these patterns stops matching
 * and every failure silently degrades to "no next step". The half that was fixable here is
 * fixed: this table no longer SELECTS PROSE — it only classifies the server's sentence into a
 * `cause` and a `remedy` key, and the sentences themselves are catalogue entries.
 *
 * The other half needs the wire. `KnowledgeDocument` (contracts `api.ts:261`) carries only
 * `statusDetail: z.string().nullable()` — free English written by the reader — and there is no
 * structural cause on it to key off. When `apps/api/src/domain/knowledge/ingestion-service.ts`
 * classifies its own failures and `KnowledgeDocumentSchema` gains a `failureCause`, this table
 * is deleted whole and `diagnoseFailure` reads that field instead. Nothing else in this file
 * or its callers moves, because they already read a key rather than a sentence.
 *
 * Until then it stays English-only on purpose: it matches the SERVER's English, which stays
 * English by D-4 (`message` is the operator's record), so it does not break under a locale
 * switch — it simply cannot be made better from this side.
 */
const PATTERNS: Array<{ cause: Exclude<FailureCause, 'unknown'>; remedy: RemedyKey; test: RegExp }> = [
  { cause: 'scan', remedy: 'scan_off', test: /OCR is off|not installed/i },
  {
    cause: 'scan',
    remedy: 'scan_unreadable',
    test: /\bscan(ned)?\b|\bocr\b|\bimage[- ]only\b|no extractable text|no legible text/i
  },
  { cause: 'legacy', remedy: 'legacy', test: /legacy Office|save it as \.docx|Rich Text/i },
  { cause: 'protected', remedy: 'protected', test: /password|encrypt|protected|permission denied/i },
  { cause: 'timeout', remedy: 'timeout', test: /exceeded \d+ ms|was stopped$|and was terminated/i },
  { cause: 'too_large', remedy: 'too_large', test: /too large to read|inflates? past|memory budget/i },
  { cause: 'empty', remedy: 'empty', test: /no indexable text|produced no text|no readable text|empty/i },
  {
    cause: 'unsupported',
    remedy: 'unsupported',
    test: /unsupported file type|not supported|cannot parse|unknown format/i
  },
  { cause: 'lost', remedy: 'lost', test: /never read|the queue lost it/i },
  { cause: 'superseded', remedy: 'superseded', test: /superseded|re-uploaded|reaped/i }
]

export const diagnoseFailure = (detail: string | null | undefined): Diagnosis => {
  if (!detail) return { cause: 'unknown', remedy: null }
  const match = PATTERNS.find((pattern) => pattern.test.test(detail))
  return match ? { cause: match.cause, remedy: match.remedy } : { cause: 'unknown', remedy: null }
}

// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

/** `synced` is the one filter that is not a standing: every document that came from a connected folder (PRD-25). */
export type DocumentFilter = 'all' | Standing | 'synced'

export const matchesFilter = (document: KnowledgeDocument, filter: DocumentFilter, query: string): boolean => {
  if (filter === 'synced' && document.origin === null) return false
  if (filter !== 'all' && filter !== 'synced' && standingOf(document) !== filter) return false
  // Folded on both sides (LOC-023): a Brazilian types "servico" for "Serviço" and "conexao"
  // for "conexão" as a matter of habit, and a plain `toLowerCase().includes()` answered both
  // with an empty table. `Intl.Collator` cannot be used here — it compares and sorts, it
  // cannot answer "does this contain that".
  const term = foldForSearch(query.trim())
  if (term === '') return true
  return foldForSearch(`${document.title} ${document.filename}`).includes(term)
}
